import {
  ArrowRight,
  Briefcase,
  Check,
  ImageIcon,
  Loader2,
  Trash2,
  User,
} from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingChoiceCard } from "@/components/onboarding/OnboardingChoiceCard";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import {
  getDgiPersonName,
  saveMyAccountantProfile,
  updateLegalData,
  uploadAccountantStudioLogo,
} from "@/services/authService";
import { cn, toTitleCase } from "@/lib/utils";

type AccountantKind = "solo" | "studio";

export function AccountantOnboardingPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState<1 | 2>(1);
  const [kind, setKind] = useState<AccountantKind>("solo");
  const [studioName, setStudioName] = useState("");
  const [studioAddress, setStudioAddress] = useState("");
  const [studioPhone, setStudioPhone] = useState("");
  const [studioLogoUrl, setStudioLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cédula de identidad — feeds the accountant's user.documentNumber
  // so the e-Factura user creation step on each of their clients can
  // reuse it as the credential the accountant will log in with.
  // Pattern mirrors the tenant-side LegalDataStep: explicit Validar
  // button on the CI row, verified-block reveals the name fields, no
  // on-blur magic (which is fiddly to get right with React form
  // libraries and what users expect).
  const [documentNumber, setDocumentNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [secondName, setSecondName] = useState("");
  const [firstLastName, setFirstLastName] = useState("");
  const [secondLastName, setSecondLastName] = useState("");
  const [validatingCi, setValidatingCi] = useState(false);
  const [dgiValidated, setDgiValidated] = useState(false);

  const cleanedCi = documentNumber.replace(/\D/g, "");
  const ciLooksValid = cleanedCi.length >= 7 && cleanedCi.length <= 8;
  const hasName =
    firstName.trim().length > 0 && firstLastName.trim().length > 0;

  const canAdvanceToIdentity =
    kind === "solo" || (kind === "studio" && studioName.trim().length > 0);
  const canValidateCi = ciLooksValid && !dgiValidated && !validatingCi;
  const canSubmit = canAdvanceToIdentity && dgiValidated && hasName;

  const handleValidateCi = async (): Promise<void> => {
    if (!canValidateCi) return;
    setValidatingCi(true);
    setDgiValidated(false);
    try {
      const data = await getDgiPersonName(cleanedCi);
      const hasResolvedName =
        (data.primerNombre?.trim()?.length ?? 0) > 0 ||
        (data.primerApellido?.trim()?.length ?? 0) > 0;
      if (!hasResolvedName) {
        toast.error(
          t(
            "accountantOnboarding.identity.notFound",
            "No encontramos esta cédula en DGI. Revisá el número.",
          ),
        );
        return;
      }
      if (data.primerNombre) setFirstName(toTitleCase(data.primerNombre));
      if (data.segundoNombre) setSecondName(toTitleCase(data.segundoNombre));
      else setSecondName("");
      if (data.primerApellido)
        setFirstLastName(toTitleCase(data.primerApellido));
      if (data.segundoApellido)
        setSecondLastName(toTitleCase(data.segundoApellido));
      else setSecondLastName("");
      setDgiValidated(true);
    } catch {
      toast.error(
        t(
          "accountantOnboarding.identity.fetchError",
          "No pudimos consultar a DGI. Probá de nuevo.",
        ),
      );
    } finally {
      setValidatingCi(false);
    }
  };

  const handleLogoSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(
        t(
          "accountantOnboarding.studio.logoTooLarge",
          "El archivo supera los 5 MB.",
        ),
      );
      return;
    }
    setUploadingLogo(true);
    try {
      const { studioLogoUrl: url } = await uploadAccountantStudioLogo(file);
      setStudioLogoUrl(url);
    } catch {
      toast.error(
        t(
          "accountantOnboarding.studio.logoUploadError",
          "No pudimos subir el logo.",
        ),
      );
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      // Persist legal identity first — the proveedor-software step on
      // every future client needs the accountant's CI as the e-Factura
      // user credential, and it has to be in place before the next
      // accountant action.
      await updateLegalData({
        firstName: firstName.trim(),
        secondName: secondName.trim() || null,
        firstLastName: firstLastName.trim(),
        secondLastName: secondLastName.trim() || null,
        documentType: "national_id",
        documentNumber: cleanedCi,
      });
      await saveMyAccountantProfile({
        kind,
        ...(kind === "studio"
          ? {
              studioName: studioName.trim(),
              ...(studioLogoUrl ? { studioLogoUrl } : {}),
              ...(studioAddress.trim()
                ? { studioAddress: studioAddress.trim() }
                : {}),
              ...(studioPhone.trim()
                ? { studioPhone: studioPhone.trim() }
                : {}),
            }
          : {}),
      });
      // Refresh the session so the AuthContext user picks up the CI we
      // just persisted — the client onboarding's "Cédula (tuya, como
      // contador)" reads user.documentNumber, which would otherwise stay
      // null until a full page reload.
      await refreshUser();
      toast.success(
        t(
          "accountantOnboarding.savedToast",
          "Listo. Te llevamos a tu cartera.",
        ),
      );
      // REMOVE (not invalidate) the ["accountant", *, "profile"] cache
      // before navigating. AccountantHomePage's gate bounces back here
      // when accountantProfile.profile is null AND it's not loading —
      // invalidate keeps the stale profile=null value in cache (so
      // loadingProfile=false) and the gate fires before the background
      // refetch lands, ping-ponging the user. removeQueries drops the
      // entry entirely, so the home page mounts in its loading state and
      // the gate is skipped until the fresh (now non-null) profile loads.
      queryClient.removeQueries({ queryKey: ["accountant"] });
      navigate("/accountant", { replace: true });
    } catch {
      toast.error(
        t("accountantOnboarding.errorToast", "No pudimos guardar tu perfil."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={step}
            total={2}
            label={t("accountantOnboarding.stepLabel", "Contador")}
          />
          {step === 1 ? (
            <>
              <h1 className="fx-onb-h1">
                {t("accountantOnboarding.titleLead", "Contanos sobre tu")}{" "}
                <span className="ital">
                  {t("accountantOnboarding.titleAccent", "práctica.")}
                </span>
              </h1>
              <p className="fx-onb-lead">
                {t(
                  "accountantOnboarding.lead",
                  "¿Trabajás solo o con un estudio? Después te pedimos tu cédula.",
                )}
              </p>

              <div className="fx-onb-choice-grid">
                <OnboardingChoiceCard
                  icon={<User aria-hidden />}
                  name={
                    <>
                      {t("accountantOnboarding.solo.nameLead", "Contador")}{" "}
                      <span className="ital">
                        {t("accountantOnboarding.solo.nameAccent", "independiente.")}
                      </span>
                    </>
                  }
                  desc={t(
                    "accountantOnboarding.solo.description",
                    "Trabajás por tu cuenta atendiendo a tus clientes.",
                  )}
                  selected={kind === "solo"}
                  onClick={() => setKind("solo")}
                />
                <OnboardingChoiceCard
                  icon={<Briefcase aria-hidden />}
                  name={
                    <>
                      {t("accountantOnboarding.studio.nameLead", "Estudio")}{" "}
                      <span className="ital">
                        {t("accountantOnboarding.studio.nameAccent", "contable.")}
                      </span>
                    </>
                  }
                  desc={t(
                    "accountantOnboarding.studio.description",
                    "Sos parte de un estudio que atiende clientes en conjunto.",
                  )}
                  selected={kind === "studio"}
                  onClick={() => setKind("studio")}
                />
              </div>
            </>
          ) : (
            <>
              <h1 className="fx-onb-h1">
                {t("accountantOnboarding.identityTitleLead", "Tu")}{" "}
                <span className="ital">
                  {t("accountantOnboarding.identityTitleAccent", "identidad.")}
                </span>
              </h1>
              <p className="fx-onb-lead">
                {t(
                  "accountantOnboarding.identityLead",
                  "Validamos tu cédula contra DGI para confirmar tu identidad como contador.",
                )}
              </p>

              <div className="fx-onb-form">
                <div className="fx-onb-form-row with-btn">
                  <div
                    className="fx-onb-field"
                    style={{ gridColumn: "span 2" }}
                  >
                    <label htmlFor="accountant-onb-ci">
                      {t(
                        "accountantOnboarding.identity.ci",
                        "Cédula de identidad",
                      )}
                    </label>
                    <input
                      id="accountant-onb-ci"
                      type="text"
                      inputMode="numeric"
                      placeholder="12345678"
                      value={documentNumber}
                      onChange={(e) => {
                        setDocumentNumber(e.target.value);
                        if (dgiValidated) setDgiValidated(false);
                      }}
                      readOnly={dgiValidated}
                      disabled={submitting}
                      autoFocus
                    />
                  </div>
                  <button
                    type="button"
                    className={cn(
                      "fx-onb-btn-validate",
                      dgiValidated && "success",
                    )}
                    onClick={handleValidateCi}
                    disabled={!canValidateCi && !dgiValidated}
                  >
                    {validatingCi ? (
                      <>
                        <Loader2 className="animate-spin" aria-hidden />
                        {t(
                          "accountantOnboarding.identity.validating",
                          "Validando…",
                        )}
                      </>
                    ) : dgiValidated ? (
                      <>
                        <Check aria-hidden />
                        {t(
                          "accountantOnboarding.identity.validated",
                          "Validado",
                        )}
                      </>
                    ) : (
                      t(
                        "accountantOnboarding.identity.validate",
                        "Validar",
                      )
                    )}
                  </button>
                </div>

                {dgiValidated ? (
                  <div className="fx-onb-verified-block">
                    <div className="vb-head">
                      <span className="title">
                        {t(
                          "accountantOnboarding.identity.confirmedLead",
                          "Tu identidad",
                        )}{" "}
                        <span className="ital">
                          {t(
                            "accountantOnboarding.identity.confirmedAccent",
                            "confirmada.",
                          )}
                        </span>
                      </span>
                      <span className="verified">
                        <span className="dot">
                          <Check aria-hidden />
                        </span>
                        {t(
                          "accountantOnboarding.identity.padronPill",
                          "Padrón nacional",
                        )}
                      </span>
                    </div>
                    <div className="vb-grid">
                      <div className="fx-onb-vb-item">
                        <div className="k">
                          {t(
                            "accountantOnboarding.identity.firstName",
                            "Primer nombre",
                          )}
                        </div>
                        <div className="v">
                          <input
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            disabled={submitting}
                            style={{
                              background: "transparent",
                              border: 0,
                              padding: 0,
                              fontSize: "14.5px",
                              fontWeight: 500,
                              color: "var(--fx-s-text)",
                              width: "100%",
                            }}
                          />
                        </div>
                      </div>
                      <div className="fx-onb-vb-item">
                        <div className="k">
                          {t(
                            "accountantOnboarding.identity.secondName",
                            "Segundo nombre",
                          )}
                        </div>
                        <div className="v">
                          <input
                            value={secondName}
                            onChange={(e) => setSecondName(e.target.value)}
                            disabled={submitting}
                            placeholder={t(
                              "accountantOnboarding.identity.optional",
                              "(opcional)",
                            )}
                            style={{
                              background: "transparent",
                              border: 0,
                              padding: 0,
                              fontSize: "14.5px",
                              fontWeight: 500,
                              color: "var(--fx-s-text)",
                              width: "100%",
                            }}
                          />
                        </div>
                      </div>
                      <div className="fx-onb-vb-item">
                        <div className="k">
                          {t(
                            "accountantOnboarding.identity.firstLastName",
                            "Primer apellido",
                          )}
                        </div>
                        <div className="v">
                          <input
                            value={firstLastName}
                            onChange={(e) => setFirstLastName(e.target.value)}
                            disabled={submitting}
                            style={{
                              background: "transparent",
                              border: 0,
                              padding: 0,
                              fontSize: "14.5px",
                              fontWeight: 500,
                              color: "var(--fx-s-text)",
                              width: "100%",
                            }}
                          />
                        </div>
                      </div>
                      <div className="fx-onb-vb-item">
                        <div className="k">
                          {t(
                            "accountantOnboarding.identity.secondLastName",
                            "Segundo apellido",
                          )}
                        </div>
                        <div className="v">
                          <input
                            value={secondLastName}
                            onChange={(e) => setSecondLastName(e.target.value)}
                            disabled={submitting}
                            placeholder={t(
                              "accountantOnboarding.identity.optional",
                              "(opcional)",
                            )}
                            style={{
                              background: "transparent",
                              border: 0,
                              padding: 0,
                              fontSize: "14.5px",
                              fontWeight: 500,
                              color: "var(--fx-s-text)",
                              width: "100%",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          )}

          {step === 1 && kind === "studio" ? (
            <div className="fx-onb-form">
              <div className="fx-onb-field">
                <label>
                  {t("accountantOnboarding.studio.logo", "Logo (opcional)")}
                </label>
                <div className="fx-onb-logo-row">
                  <div className="fx-onb-logo-preview">
                    {studioLogoUrl ? (
                      <img src={studioLogoUrl} alt="" />
                    ) : (
                      <ImageIcon aria-hidden />
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => void handleLogoSelected(e)}
                  />
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    {uploadingLogo ? (
                      <Loader2 className="animate-spin" aria-hidden />
                    ) : null}
                    {studioLogoUrl
                      ? t(
                          "accountantOnboarding.studio.logoReplace",
                          "Cambiar logo",
                        )
                      : t(
                          "accountantOnboarding.studio.logoUpload",
                          "Subir logo",
                        )}
                  </button>
                  {studioLogoUrl ? (
                    <button
                      type="button"
                      className="fx-onb-btn fx-onb-btn-secondary"
                      onClick={() => setStudioLogoUrl(null)}
                      aria-label={t(
                        "accountantOnboarding.studio.logoRemove",
                        "Quitar logo",
                      )}
                    >
                      <Trash2 aria-hidden />
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="fx-onb-field">
                <label htmlFor="studioName">
                  {t("accountantOnboarding.studio.name", "Nombre del estudio")}
                </label>
                <input
                  id="studioName"
                  type="text"
                  value={studioName}
                  onChange={(e) => setStudioName(e.target.value)}
                  placeholder={t(
                    "accountantOnboarding.studio.namePlaceholder",
                    "Estudio Pérez & Asociados",
                  )}
                />
              </div>

              <div className="fx-onb-field">
                <label htmlFor="studioAddress">
                  {t(
                    "accountantOnboarding.studio.address",
                    "Dirección (opcional)",
                  )}
                </label>
                <input
                  id="studioAddress"
                  type="text"
                  value={studioAddress}
                  onChange={(e) => setStudioAddress(e.target.value)}
                />
              </div>

              <div className="fx-onb-field">
                <label htmlFor="studioPhone">
                  {t(
                    "accountantOnboarding.studio.phone",
                    "Teléfono (opcional)",
                  )}
                </label>
                <input
                  id="studioPhone"
                  type="tel"
                  value={studioPhone}
                  onChange={(e) => setStudioPhone(e.target.value)}
                />
              </div>
            </div>
          ) : null}

          <div className="fx-onb-actions">
            <span className="side">
              {step === 2 && (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  onClick={() => setStep(1)}
                  disabled={submitting}
                >
                  {t("accountantOnboarding.back", "Volver")}
                </button>
              )}
            </span>
            <span className="side">
              {step === 1 ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-primary"
                  onClick={() => setStep(2)}
                  disabled={!canAdvanceToIdentity}
                >
                  {t("accountantOnboarding.next", "Continuar")}
                  <ArrowRight aria-hidden />
                </button>
              ) : (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-primary"
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitting}
                >
                  {submitting ? (
                    <Loader2 className="animate-spin" aria-hidden />
                  ) : null}
                  {t("accountantOnboarding.submit", "Listo")}
                  <ArrowRight aria-hidden />
                </button>
              )}
            </span>
          </div>
        </>
      }
      brand={
        <OnboardingBrandColumn
          eyebrow={t(
            "accountantOnboarding.brandEyebrow",
            "Tu cuenta de contador · ~ 1 min",
          )}
          quote={
            <>
              {t(
                "accountantOnboarding.brandQuoteLead",
                "Tu cartera de clientes,",
              )}{" "}
              <span className="accent">
                {t(
                  "accountantOnboarding.brandQuoteAccent",
                  "todo en un lugar.",
                )}
              </span>
            </>
          }
        />
      }
    />
  );
}
